import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

function makeNumberPin(n, done) {
  return `<div style="width:26px;height:26px;border-radius:9999px;background:${done ? "#10B981" : "#0F172A"};color:#fff;border:2px solid #D4AF37;display:flex;align-items:center;justify-content:center;font:700 12px sans-serif;box-shadow:0 1px 4px rgba(0,0,0,0.5);">${n}</div>`;
}

function makePin(color, approximate) {
  const border = approximate ? "2px dashed #D97706" : "2px solid #0F172A";
  return `<div style="width:16px;height:16px;border-radius:9999px;background:${color || "#D4AF37"};border:${border};box-shadow:0 1px 3px rgba(0,0,0,0.45);"></div>`;
}

export function CustomerMap({ points = [], height = 320, onSelect, testid = "customer-map" }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  // Initialise the map exactly once, and tear it down on unmount.
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { scrollWheelZoom: false }).setView([32.5, -95.5], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.markerClusterGroup({
      maxClusterRadius: 45,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      spiderfyDistanceMultiplier: 1.6,
    }).addTo(map);
    mapRef.current = map;

    // Keep tiles sized correctly when the container appears / resizes.
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(elRef.current);
    const t = setTimeout(() => map.invalidateSize(), 200);

    return () => {
      clearTimeout(t);
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Re-plot markers whenever the points change (data loads async).
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const valid = points.filter(
      (p) => typeof p.lat === "number" && typeof p.lng === "number" && !isNaN(p.lat) && !isNaN(p.lng)
    );
    const markers = [];
    valid.forEach((p) => {
      const icon = p.number != null
        ? L.divIcon({ className: "pas-pin", html: makeNumberPin(p.number, p.done), iconSize: [26, 26], iconAnchor: [13, 13] })
        : L.divIcon({ className: "pas-pin", html: makePin(p.color, p.approximate), iconSize: [16, 16], iconAnchor: [8, 8] });
      const m = L.marker([p.lat, p.lng], { icon });
      if (p.label) m.bindPopup(p.label);
      if (onSelectRef.current) m.on("click", () => onSelectRef.current(p));
      layer.addLayer(m);
      markers.push(m);
    });

    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 12);
    } else if (valid.length > 1) {
      map.fitBounds(L.featureGroup(markers).getBounds().pad(0.3), { maxZoom: 14 });
    }
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(); }, 100);
    return () => clearTimeout(t);
  }, [points]);

  return (
    <div
      ref={elRef}
      data-testid={testid}
      className="w-full border border-zinc-200 bg-zinc-100 relative z-0"
      style={{ height: `${height}px`, isolation: "isolate" }}
    />
  );
}
